import { useEffect, useRef, useState } from "react";
import { fetchOrderDetail, isAbortError } from "../services/managerApi";

/**
 * Complète les commandes visibles avec leurs kilos et leur nombre de sacs.
 *
 * `/customer/managers` ne renvoie ni l'un ni l'autre : seul le détail les
 * porte. Les colonnes Kg et Zakken affichaient donc « — » alors que le panneau
 * de détail, lui, savait les calculer — une incohérence visible à l'écran.
 *
 * En attendant que la liste les expose (point 4 du ticket backend), on va les
 * chercher commande par commande, avec trois garde-fous :
 *
 *  - seules les lignes affichées sont interrogées, pas toute la pagination ;
 *  - les résultats sont mémorisés pour la durée de la session, donc revenir
 *    sur une page déjà vue ne relance rien ;
 *  - six requêtes simultanées au maximum, pour ne pas saturer l'API ni la
 *    limite de connexions du navigateur.
 *
 * C'est un palliatif assumé : un champ dans la liste supprimerait ces N appels.
 */
const MAX_PARALLEL = 6;

export default function useOrderMetrics(orders, authUser) {
  const cache = useRef(new Map());
  const [, forceRender] = useState(0);

  useEffect(() => {
    const missing = orders.filter((order) => order.id && !cache.current.has(order.id));
    if (missing.length === 0) return undefined;

    const controller = new AbortController();
    let active = true;
    let cursor = 0;
    // Marqueurs posés avant l'appel, à retirer si l'effet est nettoyé avant la
    // réponse : les laisser ferait passer ces lignes pour « déjà chargées » et
    // leurs colonnes resteraient vides jusqu'à la fin de la session.
    const pending = new Set();
    // Capturé dans l'effet : la règle des hooks veut qu'on ne lise pas
    // `cache.current` au nettoyage. La Map est stable pour la vie du composant,
    // donc la référence reste la bonne.
    const store = cache.current;

    const runOne = async () => {
      while (cursor < missing.length) {
        const order = missing[cursor];
        cursor += 1;

        // Marqué avant l'appel : deux rendus rapprochés ne doivent pas lancer
        // deux fois la même requête.
        store.set(order.id, null);
        pending.add(order.id);

        try {
          const detail = await fetchOrderDetail({
            authUser,
            orderId: order.id,
            signal: controller.signal,
          });
          if (!active) return;
          pending.delete(order.id);
          store.set(order.id, {
            kilos: detail.kilos,
            bags: detail.bags.reduce(
              (sum, bag) => sum + (Number.parseFloat(bag.quantity) || 0),
              0
            ),
          });
        } catch (error) {
          if (!active || isAbortError(error)) return;
          // Un détail illisible ne doit pas vider la ligne : on retient
          // l'échec pour ne pas boucler dessus, la colonne reste à « — ».
          pending.delete(order.id);
          store.set(order.id, { kilos: null, bags: null });
        }
      }
    };

    Promise.all(Array.from({ length: Math.min(MAX_PARALLEL, missing.length) }, runOne)).then(
      () => {
        if (active) forceRender((count) => count + 1);
      }
    );

    return () => {
      active = false;
      controller.abort();
      pending.forEach((id) => store.delete(id));
    };
  }, [orders, authUser]);

  return (order) => {
    const metrics = cache.current.get(order.id);
    return {
      kilos: order.kilos ?? metrics?.kilos ?? null,
      bags: order.bags ?? metrics?.bags ?? null,
    };
  };
}
